// Translation support for Smart Feed.
//
// LAP-733 (GE022): the source of truth for the user's UI language is
// the i18next instance (`i18n.language`). `getUserLanguage` is now exported
// here so every Smart Feed file uses the same resolver, and we accept any
// of the legacy field names (`Langue`, `langue`, `language`) so old data
// keeps working until it is migrated.

import i18n from '../../i18n'

export const translations = {
  fr: {
    // Common
    loading: 'Chargement...',
    loadingMore: 'Chargement de nouvelles publications...',
    error: 'Erreur',
    refresh: 'Actualiser',
    filter: 'Filtrer',
    close: 'Fermer',
    save: 'Enregistrer',
    dismiss: 'Ignorer',
    like: "J'aime",
    share: 'Partager',
    later: 'Plus tard',
    allow: 'Autoriser',

    // Header / counters
    publicationsCount: '{{shown}} sur {{total}} publications',
    publicationsShown: '{{count}} sur {{count}} publications affichées',
    feedEnd: '🎉 Vous êtes arrivé à vos premières publications',

    // Weather / location
    locationAllowTitle: 'Autoriser la localisation',
    locationAllowBody: 'LiFE utilise votre position pour afficher la météo locale.',
    locationLoading: 'Chargement météo...',
    locationAllowPrompt: 'Autoriser la localisation...',
    locationDenied: 'Localisation refusée',
    locationUnavailable: 'Localisation indisponible',
    locationCurrent: 'Position actuelle',

    // Pull to refresh
    pullToRefresh: 'Tirez vers le bas pour actualiser',
    releaseToRefresh: 'Relâchez pour actualiser',

    // Publication Types
    actionable: 'Actionnable',
    reminder: 'Rappel',
    publication: 'Publication',
    summary: 'Résumé',
    news: 'Actualité',

    // Domains
    finance: 'Finance',
    time: 'Temps',
    administrative: 'Administratif',
    planet: 'Planète',
    home: 'Foyer',
    personal: 'Personnel',
    roadmap: 'Roadmap',
    professional: 'Professionnel',
    social: 'Social',

    // Subscription
    subscription: 'Abonnement',
    free: 'Gratuit',
    standard: 'Standard',
    premium: 'Premium',
    upgrade: 'Mettre à niveau',
    upgradeRequired: 'Mise à niveau requise',

    // Actions
    done: 'Terminé',
    snooze: 'Reporter',
    // LAP-726 (GE021): "Lire la suite" → "Plus"
    readMore: 'Plus',
    showLess: 'moins',
    newPublicationsPill: '{{count}} nouvelles publications',
    viewDetails: 'Voir les détails',

    // View mode (Active / Archived / Saved)
    viewActive: 'Nouvelles publications',
    viewArchived: 'Anciennes publications',
    viewSaved: 'Publications sauvegardées',

    // Filters
    all: 'Tous',
    filterByDomain: 'Filtrer par domaine',
    filterByType: 'Filtrer par type',

    // Empty states
    noPublications: 'Aucune publication',
    noPublicationsFilter: 'Aucune publication ne correspond à vos filtres',
    noPublicationsYet: 'Aucune publication pour le moment',
    pullToRefreshHint: 'Tirez vers le bas pour actualiser, ou attendez l\'actualisation automatique',

    // Errors
    loadError: 'Erreur lors du chargement',
    refreshError: 'Erreur lors de l\'actualisation',
    networkError: 'Erreur réseau. Vérifiez votre connexion et réessayez.',
    rateLimitError: 'Limite de rafraîchissements atteinte. Réessaie plus tard.',
    subscriptionRequired: 'Ce contenu nécessite un abonnement premium',

    // LAP-733 (GE022) round 10 — quick-add menu + special SmartCard labels
    quickAddDocument: 'Ajouter un document',
    quickAddProject: 'Ajouter un projet',
    quickAddTask: 'Ajouter une tâche',
    quickAddNote: 'Ajouter une note',
    quickAddOperation: 'Ajouter une opération',
    quickAddEvent: 'Ajouter un événement',
    viewAction: 'Voir',
    motivationalTitle: 'Motivation',
    reminderTitle: 'Rappel',

    // GE016 V2 — zones d'intervention + explicabilité
    zoneNow: 'Maintenant',
    zoneWatch: 'À surveiller',
    zoneProgress: 'Progrès',
    zoneDiscover: 'Découvrir',
    allClearTitle: 'Rien d\'important à signaler',
    allClearSubtitle: 'Vos indicateurs sont au vert — profitez-en.',
    whyButton: 'Pourquoi cette carte ?',
    whyTitle: 'Pourquoi cette carte ?',
    whyScoreLabel: 'Score de priorité',
    whyReasonLabel: 'Raison',
    whyFactsLabel: 'Faits observés',
    whyAiUsed: 'Texte reformulé par IA — les faits et chiffres restent calculés depuis vos données.',
    whyDeterministic: 'Contenu 100% déterministe, calculé depuis vos données.',
    whyLoadError: 'Impossible de charger l\'explication',
  },
  en: {
    loading: 'Loading...',
    loadingMore: 'Loading more publications...',
    error: 'Error',
    refresh: 'Refresh',
    filter: 'Filter',
    close: 'Close',
    save: 'Save',
    dismiss: 'Dismiss',
    like: 'Like',
    share: 'Share',
    later: 'Later',
    allow: 'Allow',

    publicationsCount: '{{shown}} of {{total}} publications',
    publicationsShown: '{{count}} of {{count}} publications shown',
    feedEnd: '🎉 You have reached your first publications',

    locationAllowTitle: 'Allow location',
    locationAllowBody: 'LiFE uses your location to display local weather.',
    locationLoading: 'Loading weather...',
    locationAllowPrompt: 'Allow location...',
    locationDenied: 'Location denied',
    locationUnavailable: 'Location unavailable',
    locationCurrent: 'Current position',

    pullToRefresh: 'Pull down to refresh',
    releaseToRefresh: 'Release to refresh',

    actionable: 'Actionable',
    reminder: 'Reminder',
    publication: 'Publication',
    summary: 'Summary',
    news: 'News',

    finance: 'Finance',
    time: 'Time',
    administrative: 'Administrative',
    planet: 'Planet',
    home: 'Home',
    personal: 'Personal',
    roadmap: 'Roadmap',
    professional: 'Professional',
    social: 'Social',

    subscription: 'Subscription',
    free: 'Free',
    standard: 'Standard',
    premium: 'Premium',
    upgrade: 'Upgrade',
    upgradeRequired: 'Upgrade Required',

    done: 'Done',
    snooze: 'Snooze',
    // LAP-726 (GE021): "Read More" → "More"
    readMore: 'More',
    showLess: 'less',
    newPublicationsPill: '{{count}} new publications',
    viewDetails: 'View Details',

    viewActive: 'Active',
    viewArchived: 'Archived',
    viewSaved: 'Saved',

    all: 'All',
    filterByDomain: 'Filter by Domain',
    filterByType: 'Filter by Type',

    noPublications: 'No publications',
    noPublicationsFilter: 'No publications match your filters',
    noPublicationsYet: 'No publications yet',
    pullToRefreshHint: 'Pull down from the top to refresh, or wait for automatic refresh',

    loadError: 'Error loading feed',
    refreshError: 'Error refreshing feed',
    networkError: 'Network error. Please check your connection and try again.',
    rateLimitError: 'Refresh limit reached. Please try again later.',
    subscriptionRequired: 'This content requires a premium subscription',

    // LAP-733 (GE022) round 10
    quickAddDocument: 'Add Document',
    quickAddProject: 'Add Project',
    quickAddTask: 'Add Task',
    quickAddNote: 'Add Note',
    quickAddOperation: 'Add Operation',
    quickAddEvent: 'Add Event',
    viewAction: 'View',
    motivationalTitle: 'Motivation',
    reminderTitle: 'Reminder',

    // GE016 V2 — intervention zones + explainability
    zoneNow: 'Now',
    zoneWatch: 'Watch',
    zoneProgress: 'Progress',
    zoneDiscover: 'Discover',
    allClearTitle: 'Nothing important to report',
    allClearSubtitle: 'Your indicators are green — enjoy it.',
    whyButton: 'Why this card?',
    whyTitle: 'Why this card?',
    whyScoreLabel: 'Priority score',
    whyReasonLabel: 'Reason',
    whyFactsLabel: 'Observed facts',
    whyAiUsed: 'Text reworded by AI — the facts and figures remain computed from your data.',
    whyDeterministic: '100% deterministic content, computed from your data.',
    whyLoadError: 'Unable to load the explanation',
  },
  de: {
    loading: 'Wird geladen...',
    loadingMore: 'Weitere Veröffentlichungen werden geladen...',
    error: 'Fehler',
    refresh: 'Aktualisieren',
    filter: 'Filter',
    close: 'Schließen',
    save: 'Speichern',
    dismiss: 'Verwerfen',
    like: 'Gefällt mir',
    share: 'Teilen',
    later: 'Später',
    allow: 'Erlauben',

    publicationsCount: '{{shown}} von {{total}} Veröffentlichungen',
    publicationsShown: '{{count}} von {{count}} angezeigten Veröffentlichungen',
    feedEnd: '🎉 Sie haben Ihre ersten Veröffentlichungen erreicht',

    locationAllowTitle: 'Standort zulassen',
    locationAllowBody: 'LiFE verwendet Ihren Standort, um das lokale Wetter anzuzeigen.',
    locationLoading: 'Wetter wird geladen...',
    locationAllowPrompt: 'Standort zulassen...',
    locationDenied: 'Standort verweigert',
    locationUnavailable: 'Standort nicht verfügbar',
    locationCurrent: 'Aktuelle Position',

    pullToRefresh: 'Zum Aktualisieren nach unten ziehen',
    releaseToRefresh: 'Loslassen zum Aktualisieren',

    actionable: 'Umsetzbar',
    reminder: 'Erinnerung',
    publication: 'Veröffentlichung',
    summary: 'Zusammenfassung',
    news: 'Aktuelles',

    finance: 'Finanzen',
    time: 'Zeit',
    administrative: 'Verwaltung',
    planet: 'Planet',
    home: 'Zuhause',
    personal: 'Persönlich',
    roadmap: 'Roadmap',
    professional: 'Beruflich',
    social: 'Soziales',

    subscription: 'Abonnement',
    free: 'Kostenlos',
    standard: 'Standard',
    premium: 'Premium',
    upgrade: 'Upgrade',
    upgradeRequired: 'Upgrade erforderlich',

    done: 'Erledigt',
    snooze: 'Zurückstellen',
    readMore: 'Mehr',
    showLess: 'weniger',
    newPublicationsPill: '{{count}} neue Veröffentlichungen',
    viewDetails: 'Details anzeigen',

    viewActive: 'Aktiv',
    viewArchived: 'Archiviert',
    viewSaved: 'Gespeichert',

    all: 'Alle',
    filterByDomain: 'Nach Domäne filtern',
    filterByType: 'Nach Typ filtern',

    noPublications: 'Keine Veröffentlichungen',
    noPublicationsFilter: 'Keine Veröffentlichungen entsprechen Ihren Filtern',
    noPublicationsYet: 'Noch keine Veröffentlichungen',
    pullToRefreshHint: 'Ziehen Sie von oben nach unten, um zu aktualisieren, oder warten Sie auf die automatische Aktualisierung',

    loadError: 'Fehler beim Laden des Feeds',
    refreshError: 'Fehler beim Aktualisieren des Feeds',
    networkError: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    rateLimitError: 'Aktualisierungslimit erreicht. Bitte versuchen Sie es später erneut.',
    subscriptionRequired: 'Dieser Inhalt erfordert ein Premium-Abonnement',

    // LAP-733 (GE022) round 10
    quickAddDocument: 'Dokument hinzufügen',
    quickAddProject: 'Projekt hinzufügen',
    quickAddTask: 'Aufgabe hinzufügen',
    quickAddNote: 'Notiz hinzufügen',
    quickAddOperation: 'Buchung hinzufügen',
    quickAddEvent: 'Ereignis hinzufügen',
    viewAction: 'Ansehen',
    motivationalTitle: 'Motivation',
    reminderTitle: 'Erinnerung',

    // GE016 V2 — Interventionszonen + Erklärbarkeit
    zoneNow: 'Jetzt',
    zoneWatch: 'Beobachten',
    zoneProgress: 'Fortschritt',
    zoneDiscover: 'Entdecken',
    allClearTitle: 'Nichts Wichtiges zu melden',
    allClearSubtitle: 'Ihre Indikatoren stehen auf Grün — genießen Sie es.',
    whyButton: 'Warum diese Karte?',
    whyTitle: 'Warum diese Karte?',
    whyScoreLabel: 'Prioritätswert',
    whyReasonLabel: 'Grund',
    whyFactsLabel: 'Beobachtete Fakten',
    whyAiUsed: 'Text von KI umformuliert — Fakten und Zahlen werden weiterhin aus Ihren Daten berechnet.',
    whyDeterministic: '100% deterministischer Inhalt, aus Ihren Daten berechnet.',
    whyLoadError: 'Erklärung konnte nicht geladen werden',
  },
  it: {
    loading: 'Caricamento...',
    loadingMore: 'Caricamento di altre pubblicazioni...',
    error: 'Errore',
    refresh: 'Aggiorna',
    filter: 'Filtra',
    close: 'Chiudi',
    save: 'Salva',
    dismiss: 'Ignora',
    like: 'Mi piace',
    share: 'Condividi',
    later: 'Più tardi',
    allow: 'Consenti',

    publicationsCount: '{{shown}} su {{total}} pubblicazioni',
    publicationsShown: '{{count}} su {{count}} pubblicazioni mostrate',
    feedEnd: '🎉 Hai raggiunto le tue prime pubblicazioni',

    locationAllowTitle: 'Consenti la posizione',
    locationAllowBody: 'LiFE utilizza la tua posizione per mostrare il meteo locale.',
    locationLoading: 'Caricamento meteo...',
    locationAllowPrompt: 'Consenti la posizione...',
    locationDenied: 'Posizione rifiutata',
    locationUnavailable: 'Posizione non disponibile',
    locationCurrent: 'Posizione attuale',

    pullToRefresh: 'Tira giù per aggiornare',
    releaseToRefresh: 'Rilascia per aggiornare',

    actionable: 'Azionabile',
    reminder: 'Promemoria',
    publication: 'Pubblicazione',
    summary: 'Riepilogo',
    news: 'Notizie',

    finance: 'Finanza',
    time: 'Tempo',
    administrative: 'Amministrativo',
    planet: 'Pianeta',
    home: 'Casa',
    personal: 'Personale',
    roadmap: 'Roadmap',
    professional: 'Professionale',
    social: 'Sociale',

    subscription: 'Abbonamento',
    free: 'Gratuito',
    standard: 'Standard',
    premium: 'Premium',
    upgrade: 'Aggiorna',
    upgradeRequired: 'Aggiornamento richiesto',

    done: 'Completato',
    snooze: 'Posticipa',
    readMore: 'Altro',
    showLess: 'meno',
    newPublicationsPill: '{{count}} nuove pubblicazioni',
    viewDetails: 'Vedi dettagli',

    viewActive: 'Attivo',
    viewArchived: 'Archiviato',
    viewSaved: 'Salvato',

    all: 'Tutti',
    filterByDomain: 'Filtra per dominio',
    filterByType: 'Filtra per tipo',

    noPublications: 'Nessuna pubblicazione',
    noPublicationsFilter: 'Nessuna pubblicazione corrisponde ai tuoi filtri',
    noPublicationsYet: 'Ancora nessuna pubblicazione',
    pullToRefreshHint: 'Tira giù dall\'alto per aggiornare o attendi l\'aggiornamento automatico',

    loadError: 'Errore durante il caricamento del feed',
    refreshError: 'Errore durante l\'aggiornamento del feed',
    networkError: 'Errore di rete. Controlla la connessione e riprova.',
    rateLimitError: 'Limite di aggiornamenti raggiunto. Riprova più tardi.',
    subscriptionRequired: 'Questo contenuto richiede un abbonamento premium',

    // LAP-733 (GE022) round 10
    quickAddDocument: 'Aggiungi documento',
    quickAddProject: 'Aggiungi progetto',
    quickAddTask: 'Aggiungi attività',
    quickAddNote: 'Aggiungi nota',
    quickAddOperation: 'Aggiungi operazione',
    quickAddEvent: 'Aggiungi evento',
    viewAction: 'Visualizza',
    motivationalTitle: 'Motivazione',
    reminderTitle: 'Promemoria',

    // GE016 V2 — zone di intervento + spiegabilità
    zoneNow: 'Adesso',
    zoneWatch: 'Da monitorare',
    zoneProgress: 'Progressi',
    zoneDiscover: 'Scopri',
    allClearTitle: 'Niente di importante da segnalare',
    allClearSubtitle: 'I tuoi indicatori sono verdi — goditelo.',
    whyButton: 'Perché questa scheda?',
    whyTitle: 'Perché questa scheda?',
    whyScoreLabel: 'Punteggio di priorità',
    whyReasonLabel: 'Motivo',
    whyFactsLabel: 'Fatti osservati',
    whyAiUsed: 'Testo riformulato dall\'IA — fatti e cifre restano calcolati dai tuoi dati.',
    whyDeterministic: 'Contenuto 100% deterministico, calcolato dai tuoi dati.',
    whyLoadError: 'Impossibile caricare la spiegazione',
  },
  es: {
    loading: 'Cargando...',
    loadingMore: 'Cargando más publicaciones...',
    error: 'Error',
    refresh: 'Actualizar',
    filter: 'Filtrar',
    close: 'Cerrar',
    save: 'Guardar',
    dismiss: 'Descartar',
    like: 'Me gusta',
    share: 'Compartir',
    later: 'Más tarde',
    allow: 'Permitir',

    publicationsCount: '{{shown}} de {{total}} publicaciones',
    publicationsShown: '{{count}} de {{count}} publicaciones mostradas',
    feedEnd: '🎉 Has llegado a tus primeras publicaciones',

    locationAllowTitle: 'Permitir ubicación',
    locationAllowBody: 'LiFE usa tu ubicación para mostrar el clima local.',
    locationLoading: 'Cargando el clima...',
    locationAllowPrompt: 'Permitir ubicación...',
    locationDenied: 'Ubicación denegada',
    locationUnavailable: 'Ubicación no disponible',
    locationCurrent: 'Posición actual',

    pullToRefresh: 'Tira hacia abajo para actualizar',
    releaseToRefresh: 'Suelta para actualizar',

    actionable: 'Accionable',
    reminder: 'Recordatorio',
    publication: 'Publicación',
    summary: 'Resumen',
    news: 'Noticias',

    finance: 'Finanzas',
    time: 'Tiempo',
    administrative: 'Administrativo',
    planet: 'Planeta',
    home: 'Hogar',
    personal: 'Personal',
    roadmap: 'Roadmap',
    professional: 'Profesional',
    social: 'Social',

    subscription: 'Suscripción',
    free: 'Gratis',
    standard: 'Estándar',
    premium: 'Premium',
    upgrade: 'Actualizar',
    upgradeRequired: 'Actualización requerida',

    done: 'Hecho',
    snooze: 'Posponer',
    readMore: 'Más',
    showLess: 'menos',
    newPublicationsPill: '{{count}} nuevas publicaciones',
    viewDetails: 'Ver detalles',

    viewActive: 'Activo',
    viewArchived: 'Archivado',
    viewSaved: 'Guardado',

    all: 'Todos',
    filterByDomain: 'Filtrar por dominio',
    filterByType: 'Filtrar por tipo',

    noPublications: 'Sin publicaciones',
    noPublicationsFilter: 'Ninguna publicación coincide con tus filtros',
    noPublicationsYet: 'Aún no hay publicaciones',
    pullToRefreshHint: 'Tira hacia abajo desde arriba para actualizar o espera la actualización automática',

    loadError: 'Error al cargar el feed',
    refreshError: 'Error al actualizar el feed',
    networkError: 'Error de red. Comprueba tu conexión e inténtalo de nuevo.',
    rateLimitError: 'Límite de actualizaciones alcanzado. Inténtalo de nuevo más tarde.',
    subscriptionRequired: 'Este contenido requiere una suscripción premium',

    // LAP-733 (GE022) round 10
    quickAddDocument: 'Añadir documento',
    quickAddProject: 'Añadir proyecto',
    quickAddTask: 'Añadir tarea',
    quickAddNote: 'Añadir nota',
    quickAddOperation: 'Añadir operación',
    quickAddEvent: 'Añadir evento',
    viewAction: 'Ver',
    motivationalTitle: 'Motivación',
    reminderTitle: 'Recordatorio',

    // GE016 V2 — zonas de intervención + explicabilidad
    zoneNow: 'Ahora',
    zoneWatch: 'Vigilar',
    zoneProgress: 'Progreso',
    zoneDiscover: 'Descubrir',
    allClearTitle: 'Nada importante que señalar',
    allClearSubtitle: 'Tus indicadores están en verde — disfrútalo.',
    whyButton: '¿Por qué esta tarjeta?',
    whyTitle: '¿Por qué esta tarjeta?',
    whyScoreLabel: 'Puntuación de prioridad',
    whyReasonLabel: 'Motivo',
    whyFactsLabel: 'Hechos observados',
    whyAiUsed: 'Texto reformulado por IA — los hechos y cifras siguen calculados desde tus datos.',
    whyDeterministic: 'Contenido 100% determinista, calculado desde tus datos.',
    whyLoadError: 'No se pudo cargar la explicación',
  },
}

// LAP-733 (GE022) round 13 — full zh/ja/ar coverage to match the FR baseline.
// Previously these locales spread `...translations.en` and only overrode ~25
// keys, leaving the remaining ~55 strings rendered in English at runtime.
translations.zh = {
  ...translations.en,
  // Common
  loading: '加载中...',
  loadingMore: '正在加载更多动态...',
  error: '错误',
  refresh: '刷新',
  filter: '筛选',
  close: '关闭',
  save: '保存',
  dismiss: '忽略',
  like: '喜欢',
  share: '分享',
  later: '稍后',
  allow: '允许',

  // Header / counters
  publicationsCount: '{{shown}}/{{total}} 条动态',
  publicationsShown: '已显示 {{count}}/{{count}} 条动态',
  feedEnd: '🎉 你已到达最早的动态',

  // Weather / location
  locationAllowTitle: '允许定位',
  locationAllowBody: 'LiFE 使用你的位置显示本地天气。',
  locationLoading: '正在加载天气...',
  locationAllowPrompt: '允许定位...',
  locationDenied: '定位被拒绝',
  locationUnavailable: '定位不可用',
  locationCurrent: '当前位置',

  // Pull to refresh
  pullToRefresh: '下拉刷新',
  releaseToRefresh: '松开以刷新',

  // Publication types
  actionable: '可执行',
  reminder: '提醒',
  publication: '动态',
  summary: '摘要',
  news: '资讯',

  // Domains
  finance: '财务',
  time: '时间',
  administrative: '行政',
  planet: '地球',
  home: '家居',
  personal: '个人',
  roadmap: '路线图',
  professional: '职业',
  social: '社交',

  // Subscription
  subscription: '订阅',
  free: '免费',
  standard: '标准',
  premium: '高级',
  upgrade: '升级',
  upgradeRequired: '需要升级',

  // Actions
  done: '完成',
  snooze: '稍后',
  readMore: '更多',
  showLess: '收起',
  newPublicationsPill: '{{count}} 条新动态',
  viewDetails: '查看详情',

  // View mode
  viewActive: '新动态',
  viewArchived: '历史动态',
  viewSaved: '已保存',

  // Filters
  all: '全部',
  filterByDomain: '按领域筛选',
  filterByType: '按类型筛选',

  // Empty states
  noPublications: '暂无动态',
  noPublicationsFilter: '没有动态匹配当前筛选',
  noPublicationsYet: '目前还没有动态',
  pullToRefreshHint: '从顶部下拉刷新,或等待自动刷新',

  // Errors
  loadError: '加载动态时出错',
  refreshError: '刷新动态时出错',
  networkError: '网络错误。请检查连接后重试。',
  rateLimitError: '已达到刷新次数上限。请稍后再试。',
  subscriptionRequired: '此内容需要高级订阅',

  // Quick-add menu + special SmartCard labels
  quickAddDocument: '添加文档',
  quickAddProject: '添加项目',
  quickAddTask: '添加任务',
  quickAddNote: '添加笔记',
  quickAddOperation: '添加交易',
  quickAddEvent: '添加事件',
  viewAction: '查看',
  motivationalTitle: '激励',
  reminderTitle: '提醒',

  // GE016 V2 — 干预分区 + 可解释性
  zoneNow: '现在',
  zoneWatch: '关注',
  zoneProgress: '进展',
  zoneDiscover: '发现',
  allClearTitle: '没有重要事项',
  allClearSubtitle: '你的指标一切正常——好好享受。',
  whyButton: '为什么显示这张卡片?',
  whyTitle: '为什么显示这张卡片?',
  whyScoreLabel: '优先级分数',
  whyReasonLabel: '原因',
  whyFactsLabel: '观察到的事实',
  whyAiUsed: '文字由 AI 改写——事实和数字仍由你的数据计算得出。',
  whyDeterministic: '内容 100% 确定性,由你的数据计算得出。',
  whyLoadError: '无法加载解释',
}

translations.ja = {
  ...translations.en,
  // Common
  loading: '読み込み中...',
  loadingMore: 'さらに投稿を読み込み中...',
  error: 'エラー',
  refresh: '更新',
  filter: 'フィルター',
  close: '閉じる',
  save: '保存',
  dismiss: '非表示',
  like: 'いいね',
  share: '共有',
  later: '後で',
  allow: '許可',

  // Header / counters
  publicationsCount: '{{shown}}/{{total}} 件の投稿',
  publicationsShown: '{{count}}/{{count}} 件の投稿を表示中',
  feedEnd: '🎉 最初の投稿に到達しました',

  // Weather / location
  locationAllowTitle: '位置情報を許可',
  locationAllowBody: 'LiFE は地域の天気表示のためにあなたの位置情報を使用します。',
  locationLoading: '天気を読み込み中...',
  locationAllowPrompt: '位置情報を許可...',
  locationDenied: '位置情報が拒否されました',
  locationUnavailable: '位置情報を利用できません',
  locationCurrent: '現在地',

  // Pull to refresh
  pullToRefresh: '下に引いて更新',
  releaseToRefresh: '離して更新',

  // Publication types
  actionable: 'アクション',
  reminder: 'リマインダー',
  publication: '投稿',
  summary: 'サマリー',
  news: 'ニュース',

  // Domains
  finance: 'ファイナンス',
  time: 'タイム',
  administrative: '行政',
  planet: 'プラネット',
  home: 'ホーム',
  personal: 'パーソナル',
  roadmap: 'ロードマップ',
  professional: 'プロフェッショナル',
  social: 'ソーシャル',

  // Subscription
  subscription: 'サブスクリプション',
  free: '無料',
  standard: 'スタンダード',
  premium: 'プレミアム',
  upgrade: 'アップグレード',
  upgradeRequired: 'アップグレードが必要',

  // Actions
  done: '完了',
  snooze: '後で',
  readMore: 'もっと',
  showLess: '閉じる',
  newPublicationsPill: '新しい投稿 {{count}} 件',
  viewDetails: '詳細を見る',

  // View mode
  viewActive: '新着',
  viewArchived: 'アーカイブ',
  viewSaved: '保存済み',

  // Filters
  all: 'すべて',
  filterByDomain: '分野で絞り込み',
  filterByType: '種類で絞り込み',

  // Empty states
  noPublications: '投稿はありません',
  noPublicationsFilter: 'フィルターに一致する投稿はありません',
  noPublicationsYet: 'まだ投稿はありません',
  pullToRefreshHint: '上部から下に引いて更新するか、自動更新をお待ちください',

  // Errors
  loadError: 'フィードの読み込みエラー',
  refreshError: 'フィードの更新エラー',
  networkError: 'ネットワークエラーです。接続を確認して再度お試しください。',
  rateLimitError: '更新回数の上限に達しました。しばらくしてから再度お試しください。',
  subscriptionRequired: 'このコンテンツにはプレミアムサブスクリプションが必要です',

  // Quick-add menu + special SmartCard labels
  quickAddDocument: '書類を追加',
  quickAddProject: 'プロジェクトを追加',
  quickAddTask: 'タスクを追加',
  quickAddNote: 'メモを追加',
  quickAddOperation: '取引を追加',
  quickAddEvent: 'イベントを追加',
  viewAction: '表示',
  motivationalTitle: 'モチベーション',
  reminderTitle: 'リマインダー',

  // GE016 V2 — 介入ゾーン + 説明可能性
  zoneNow: '今すぐ',
  zoneWatch: '要注意',
  zoneProgress: '進捗',
  zoneDiscover: '発見',
  allClearTitle: '重要なお知らせはありません',
  allClearSubtitle: 'すべての指標は良好です — この状態をお楽しみください。',
  whyButton: 'なぜこのカード?',
  whyTitle: 'なぜこのカード?',
  whyScoreLabel: '優先度スコア',
  whyReasonLabel: '理由',
  whyFactsLabel: '観測された事実',
  whyAiUsed: '文章は AI により言い換えられていますが、事実と数値はあなたのデータから計算されています。',
  whyDeterministic: '100% 決定論的なコンテンツで、あなたのデータから計算されています。',
  whyLoadError: '説明を読み込めませんでした',
}

translations.ar = {
  ...translations.en,
  // Common
  loading: 'جارٍ التحميل...',
  loadingMore: 'جارٍ تحميل المزيد من المنشورات...',
  error: 'خطأ',
  refresh: 'تحديث',
  filter: 'تصفية',
  close: 'إغلاق',
  save: 'حفظ',
  dismiss: 'تجاهل',
  like: 'إعجاب',
  share: 'مشاركة',
  later: 'لاحقًا',
  allow: 'السماح',

  // Header / counters
  publicationsCount: '{{shown}} من {{total}} منشور',
  publicationsShown: 'تم عرض {{count}} من {{count}} منشور',
  feedEnd: '🎉 لقد وصلت إلى أوّل منشوراتك',

  // Weather / location
  locationAllowTitle: 'السماح بالموقع',
  locationAllowBody: 'يستخدم LiFE موقعك لعرض الطقس المحلي.',
  locationLoading: 'جارٍ تحميل الطقس...',
  locationAllowPrompt: 'السماح بالموقع...',
  locationDenied: 'تم رفض الموقع',
  locationUnavailable: 'الموقع غير متاح',
  locationCurrent: 'الموقع الحالي',

  // Pull to refresh
  pullToRefresh: 'اسحب للأسفل للتحديث',
  releaseToRefresh: 'حرّر للتحديث',

  // Publication types
  actionable: 'قابل للتنفيذ',
  reminder: 'تذكير',
  publication: 'منشور',
  summary: 'ملخص',
  news: 'أخبار',

  // Domains
  finance: 'المالية',
  time: 'الوقت',
  administrative: 'الإدارة',
  planet: 'الكوكب',
  home: 'المنزل',
  personal: 'شخصي',
  roadmap: 'خارطة الطريق',
  professional: 'المهني',
  social: 'الاجتماعي',

  // Subscription
  subscription: 'الاشتراك',
  free: 'مجاني',
  standard: 'قياسي',
  premium: 'بريميوم',
  upgrade: 'الترقية',
  upgradeRequired: 'الترقية مطلوبة',

  // Actions
  done: 'تم',
  snooze: 'تأجيل',
  readMore: 'المزيد',
  showLess: 'أقل',
  newPublicationsPill: '{{count}} منشورات جديدة',
  viewDetails: 'عرض التفاصيل',

  // View mode
  viewActive: 'جديد',
  viewArchived: 'الأرشيف',
  viewSaved: 'المحفوظ',

  // Filters
  all: 'الكل',
  filterByDomain: 'تصفية حسب المجال',
  filterByType: 'تصفية حسب النوع',

  // Empty states
  noPublications: 'لا توجد منشورات',
  noPublicationsFilter: 'لا توجد منشورات تطابق التصفيات',
  noPublicationsYet: 'لا توجد منشورات بعد',
  pullToRefreshHint: 'اسحب من الأعلى للتحديث أو انتظر التحديث التلقائي',

  // Errors
  loadError: 'خطأ أثناء تحميل الخلاصة',
  refreshError: 'خطأ أثناء تحديث الخلاصة',
  networkError: 'خطأ في الشبكة. يرجى التحقق من الاتصال والمحاولة مرة أخرى.',
  rateLimitError: 'تم بلوغ حد التحديثات. يرجى المحاولة لاحقاً.',
  subscriptionRequired: 'يتطلب هذا المحتوى اشتراكًا بريميوم',

  // Quick-add menu + special SmartCard labels
  quickAddDocument: 'إضافة مستند',
  quickAddProject: 'إضافة مشروع',
  quickAddTask: 'إضافة مهمة',
  quickAddNote: 'إضافة ملاحظة',
  quickAddOperation: 'إضافة عملية',
  quickAddEvent: 'إضافة حدث',
  viewAction: 'عرض',
  motivationalTitle: 'تحفيز',
  reminderTitle: 'تذكير',

  // GE016 V2 — مناطق التدخل + قابلية التفسير
  zoneNow: 'الآن',
  zoneWatch: 'للمتابعة',
  zoneProgress: 'التقدم',
  zoneDiscover: 'اكتشف',
  allClearTitle: 'لا شيء مهم للإبلاغ عنه',
  allClearSubtitle: 'مؤشراتك خضراء — استمتع بذلك.',
  whyButton: 'لماذا هذه البطاقة؟',
  whyTitle: 'لماذا هذه البطاقة؟',
  whyScoreLabel: 'درجة الأولوية',
  whyReasonLabel: 'السبب',
  whyFactsLabel: 'الوقائع الملحوظة',
  whyAiUsed: 'أعيدت صياغة النص بالذكاء الاصطناعي — تظل الوقائع والأرقام محسوبة من بياناتك.',
  whyDeterministic: 'محتوى حتمي 100% محسوب من بياناتك.',
  whyLoadError: 'تعذّر تحميل التفسير',
}

const SUPPORTED = new Set(['fr', 'en', 'de', 'it', 'es', 'zh', 'ja', 'ar'])
// Some legacy data stored full names in user_parameter.Langue.
const LEGACY_NAME_MAP = {
  french: 'fr', francais: 'fr', français: 'fr',
  english: 'en', anglais: 'en',
  german: 'de', deutsch: 'de', allemand: 'de',
  italian: 'it', italiano: 'it', italien: 'it',
  spanish: 'es', español: 'es', espanol: 'es', espagnol: 'es',
  chinese: 'zh', chinois: 'zh', '中文': 'zh',
  japanese: 'ja', japonais: 'ja', '日本語': 'ja',
  arabic: 'ar', arabe: 'ar', 'العربية': 'ar',
}

const normalizeLanguage = (raw) => {
  if (!raw || typeof raw !== 'string') return 'fr'
  const lower = raw.toLowerCase().trim()
  if (LEGACY_NAME_MAP[lower]) return LEGACY_NAME_MAP[lower]
  const short = lower.split('-')[0]
  return SUPPORTED.has(short) ? short : 'fr'
}

/**
 * Resolve the active UI language for the Smart Feed.
 *
 * Order of precedence (LAP-733):
 *   1. i18next.language — the live UI language, kept in sync with the
 *      LanguageSelector and the user's stored preference.
 *   2. siteUser.user.language — the new persisted preference.
 *   3. Legacy fields user_parameter.Langue / langue (free-text, "French" etc.)
 *   4. navigator.language
 *   5. 'fr' as the final fallback (matches i18n.fallbackLng).
 */
export const getUserLanguage = (siteUser) => {
  const fromI18n = i18n?.language
  if (fromI18n) {
    const normalized = normalizeLanguage(fromI18n)
    if (SUPPORTED.has(normalized)) return normalized
  }

  const userLang = siteUser?.user?.language
  if (userLang) {
    const normalized = normalizeLanguage(userLang)
    if (SUPPORTED.has(normalized)) return normalized
  }

  const param = siteUser?.user?.user_parameter
  const legacy = param?.Langue || param?.langue
  if (legacy) {
    const normalized = normalizeLanguage(legacy)
    if (SUPPORTED.has(normalized)) return normalized
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    const normalized = normalizeLanguage(navigator.language)
    if (SUPPORTED.has(normalized)) return normalized
  }

  return 'fr'
}

const interpolate = (template, vars) => {
  if (!vars || typeof template !== 'string') return template
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
    vars[key] !== undefined ? String(vars[key]) : match
  )
}

export const getTranslation = (key, language = 'fr', vars = null) => {
  const normalized = normalizeLanguage(language)
  const langTranslations = translations[normalized] || translations.fr
  const value = langTranslations[key] != null ? langTranslations[key] : key
  return interpolate(value, vars)
}

export const translateText = (text, language = 'fr') => {
  if (typeof text !== 'string') return text
  if (text.startsWith('t:')) {
    return getTranslation(text.substring(2), language)
  }
  return text
}

export default translations
